import {
  Component,
  OnInit,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';
import { TemplateEditSubmitDataModel } from '../shared/models/template-edit-submit-data-model';
import { SettingsService } from '../shared/services/settings.service';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import {
  UploaderOptions,
  UploadInput,
  UploadOutput,
} from '@angular-ex/uploader';
import { UrlConfigContainer } from '@admin/core/url-config';
import { UrlConfigService } from '@hrra/core';
import { Router, ActivatedRoute } from '@angular/router';
import { PromoEmailAttachmentModel } from '../shared/models/promo-email-attachment-model';
import { FieldState } from '@admin/shared/models/field-state';
import { Validator, IValidator } from '@admin/shared/validation/validators';
import { ValidationUtils } from '@admin/shared/validation/validation-runner';
import { PortfolioService } from '../../portfolios/shared/services/portfolio.service';
import { PortfolioModel } from '../../portfolios/shared/models/portfolio-model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-email-template-editor',
  templateUrl: './email-template-editor.component.html',
  styleUrls: ['./email-template-editor.component.less'],
  standalone: false,
})
export class EmailTemplateEditorComponent implements OnInit {
  templateData: TemplateEditSubmitDataModel;

  private edit: boolean;
  public specialDateFormat: string;
  uploadOptions: UploaderOptions;
  uploadInput: EventEmitter<UploadInput>;

  portfolios: PortfolioModel[];
  selectedPortfolios: PortfolioModel[];
  lastSelectedPortfolioId: number;

  fieldStates: {
    dates: FieldState;
    common: FieldState;
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private config: ConfigurationService,
    private settingsService: SettingsService,
    private portfolioService: PortfolioService,
    private translationService: TranslateService,
    private urlService: UrlConfigService<UrlConfigContainer>
  ) {
    this.edit = false;
    this.templateData = new TemplateEditSubmitDataModel();
    this.uploadOptions = { concurrency: 1 };
    this.uploadInput = new EventEmitter<UploadInput>();
    this.portfolios = [];
    this.selectedPortfolios = [];
  }

  onSave() {
    this.invokeValidation(this.getValidators(), () => {
      this.onSaveHelper().subscribe((r) => {
        if (r.success) {
          this.navigateToParent();
        }
      });
    });
  }

  private onSaveHelper() {
    this.templateData.portfolios = this.selectedPortfolios.map(
      (p) => p.portfolioId
    );

    if (this.edit)
      return this.settingsService.EditEmailTemplate(this.templateData);

    return this.settingsService.CreateEmailTemplate(this.templateData);
  }

  onCancel() {
    this.navigateToParent();
  }

  onAttachmentDelete(file: PromoEmailAttachmentModel) {
    file.isDeleted = true;
  }

  onAttachmentRestore(file: PromoEmailAttachmentModel) {
    file.isDeleted = false;
  }

  onUploadOutput(output: UploadOutput): void {
    if (output.type === 'allAddedToQueue') {
      const event: UploadInput = {
        type: 'uploadAll',
        url: this.urlService.urlConfig.api.data.file.upload,
        method: 'POST',
      };
      this.uploadInput.emit(event);
    } else if (output.type === 'done' && output.file.responseStatus == 200) {
      let newAttachment = new PromoEmailAttachmentModel(
        output.file.response.fileName,
        output.file.name
      );
      this.templateData.attachments.push(newAttachment);
    }
  }

  onPortfolioChange() {
    if (this.lastSelectedPortfolioId) {
      let portfolio = this.portfolios.filter(
        (p) => p.portfolioId == this.lastSelectedPortfolioId
      )[0];
      let alreadyAdded = this.selectedPortfolios.some(
        (p) => p.portfolioId == portfolio.portfolioId
      );
      if (portfolio && !alreadyAdded) {
        this.selectedPortfolios.push(portfolio);
      }
      this.lastSelectedPortfolioId = null;
    }
  }

  onPortfolioDelete(portfolio: PortfolioModel) {
    let index = this.selectedPortfolios.indexOf(portfolio);
    if (index > -1) {
      this.selectedPortfolios.splice(index, 1);
    }
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.specialDateFormat = this.getSpecialDateFormat(config.dateFormat);
    this.portfolioService.getPortfolios().subscribe((r) => {
      this.portfolios = r.items;
    });

    this.route.params.subscribe((p) => {
      let templateId = p['id'];
      if (templateId) {
        this.edit = true;
        this.initData(templateId);
      }
    });

    this.initializeFieldsState();
  }

  private initData(templateId: number) {
    this.settingsService.GetEmailTemplate(templateId).subscribe((t) => {
      this.templateData.promoEmailTemplateId = t.promoEmailTemplateId;
      this.templateData.name = t.name;
      this.templateData.subject = t.subject;
      this.templateData.content = t.content;
      this.templateData.validFrom = new Date(t.validFrom);
      this.templateData.validTo = new Date(t.validTo);
      this.templateData.attachments = t.attachments;
      this.selectedPortfolios = t.portfolios;
    });
  }

  private navigateToParent() {
    this.router.navigate(['/settings/template-list']);
  }

  private getValidators() {
    let emptyMessage = this.translationService.instant(
      'message.EmptyFieldDetected'
    );
    let IncorrectDates = this.translationService.instant(
      'message.StartDateMustNotComeAfterEndDate'
    );

    let data = this.templateData;
    let fields = this.fieldStates;

    return [
      Validator.forRequired(
        fields.common,
        () =>
          data.name &&
          data.subject &&
          data.content &&
          data.validFrom &&
          data.validTo,
        emptyMessage
      ),
      Validator.forMinLength(
        fields.common,
        () => this.selectedPortfolios,
        1,
        emptyMessage
      ),
      Validator.forPredicate(
        fields.dates,
        () => data.validFrom < data.validTo,
        IncorrectDates
      ),
    ];
  }

  private invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject() {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private initializeFieldsState() {
    this.fieldStates = {
      dates: new FieldState(() => this.templateData.validFrom),
      common: new FieldState(() => this.templateData.name),
    };
  }
}
